import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  mcGetOverview, mcGetTransactions, mcGetTokenTransfers, mcGetInternal,
  mcGetPortfolio, mcGetDexTrades, mcGetPnl,
} from '../lib/api';
import { copyToClipboard } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ShareToTelegram } from '../components/ShareToTelegram';
import {
  ArrowLeft, Copy, Check, ExternalLink, Wallet, Clock, Droplets,
  ArrowDownLeft, ArrowUpRight, ArrowLeftRight, RefreshCw, Coins,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------- formatters ----------
function fmtUsd(v) {
  const n = parseFloat(v ?? 0);
  if (!isFinite(n)) return '$0.00';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  if (Math.abs(n) < 0.01 && n !== 0) return `$${n.toFixed(6)}`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtNum(v) {
  const n = parseFloat(v ?? 0);
  if (!isFinite(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (Math.abs(n) >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return n === 0 ? '0' : n.toFixed(6);
}
function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const trunc = (a, n = 6) => (a ? `${a.slice(0, n + 2)}…${a.slice(-4)}` : '—');

// ---------- shared bits ----------
const AddrLink = ({ chain, address, self }) => {
  if (!address) return <span className="text-muted-foreground">—</span>;
  if (self) return <span className="font-mono text-xs text-muted-foreground">self</span>;
  return (
    <Link to={`/explorer/${chain}/address/${address}`} className="font-mono text-xs text-primary hover:underline">
      {trunc(address)}
    </Link>
  );
};

const HashLink = ({ explorer, hash }) => (
  <a
    href={`${explorer}/tx/${hash}`}
    target="_blank" rel="noreferrer"
    className="font-mono text-xs text-primary hover:underline"
  >
    {trunc(hash, 8)}
  </a>
);

const DirBadge = ({ d }) => (
  d === 'in'
    ? <span className="inline-flex items-center gap-1 text-emerald-500 text-xs"><ArrowDownLeft className="w-3 h-3" />IN</span>
    : d === 'out'
      ? <span className="inline-flex items-center gap-1 text-red-500 text-xs"><ArrowUpRight className="w-3 h-3" />OUT</span>
      : <span className="inline-flex items-center gap-1 text-muted-foreground text-xs"><ArrowLeftRight className="w-3 h-3" />SELF</span>
);

const LoadMore = ({ hasMore, loading, onClick, testid }) => (
  hasMore ? (
    <div className="flex justify-center py-3">
      <Button variant="outline" size="sm" onClick={onClick} disabled={loading} data-testid={testid}>
        {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Load more
      </Button>
    </div>
  ) : null
);

const EmptyState = ({ text }) => (
  <div className="text-center py-10 text-sm text-muted-foreground">{text}</div>
);

const UnsupportedNote = ({ reason }) => (
  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-500 text-sm p-4" data-testid="unsupported-note">
    ⚠ {reason || 'Not available on this chain yet.'}
  </div>
);

// ---------- paged list hook ----------
function usePaged(fetchPage) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const d = await fetchPage(p);
      setMeta(d);
      setRows((r) => (p === 1 ? d.rows || [] : [...r, ...(d.rows || [])]));
      setHasMore(!!d.has_more);
      setPage(p);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => { load(1); }, [load]);
  return { rows, loading, hasMore, meta, loadMore: () => load(page + 1) };
}

// ---------- tabs ----------
function TxTab({ chain, address, explorer }) {
  const fetchPage = useCallback((p) => mcGetTransactions(chain, address, { page: p, offset: 25 }), [chain, address]);
  const { rows, loading, hasMore, meta, loadMore } = usePaged(fetchPage);
  if (meta?.unsupported) return <UnsupportedNote reason={meta.reason} />;
  if (!loading && rows.length === 0) {
    return meta?.explorer_error
      ? <UnsupportedNote reason={meta.explorer_error} />
      : <EmptyState text="No transactions found" />;
  }
  return (
    <div className="overflow-x-auto" data-testid="mc-tx-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
          <th className="py-2 pr-3 whitespace-nowrap">Tx Hash</th><th className="py-2 pr-3">Method</th>
          <th className="py-2 pr-3">Age</th><th className="py-2 pr-3">From</th><th className="py-2 pr-3"></th>
          <th className="py-2 pr-3">To</th><th className="py-2 pr-3 text-right">Value</th><th className="py-2 text-right">Fee</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.hash}-${i}`} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 pr-3 whitespace-nowrap">
                <HashLink explorer={explorer} hash={r.hash} />
                {r.status === 'failed' && <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0 text-red-500 border-red-500/50">FAIL</Badge>}
              </td>
              <td className="py-2 pr-3"><span className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{r.method || '—'}</span></td>
              <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap" title={r.time}>{fmtTime(r.time)}</td>
              <td className="py-2 pr-3 whitespace-nowrap"><AddrLink chain={chain} address={r.from} self={r.from === address} /></td>
              <td className="py-2 pr-3"><DirBadge d={r.direction} /></td>
              <td className="py-2 pr-3 whitespace-nowrap"><AddrLink chain={chain} address={r.to} self={r.to === address} /></td>
              <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtNum(r.value)}</td>
              <td className="py-2 text-right font-mono text-xs text-muted-foreground whitespace-nowrap">{r.fee ? r.fee.toFixed(6) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && rows.length === 0 && <Skeleton className="h-40 w-full mt-2" />}
      <LoadMore hasMore={hasMore} loading={loading} onClick={loadMore} testid="tx-load-more-btn" />
    </div>
  );
}

function TransfersTab({ chain, address, explorer }) {
  const fetchPage = useCallback((p) => mcGetTokenTransfers(chain, address, { page: p, offset: 25 }), [chain, address]);
  const { rows, loading, hasMore, meta, loadMore } = usePaged(fetchPage);
  if (meta?.unsupported) return <UnsupportedNote reason={meta.reason} />;
  if (!loading && rows.length === 0) {
    return meta?.explorer_error
      ? <UnsupportedNote reason={meta.explorer_error} />
      : <EmptyState text="No token transfers found" />;
  }
  return (
    <div className="overflow-x-auto" data-testid="mc-transfers-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
          <th className="py-2 pr-3">Tx Hash</th><th className="py-2 pr-3">Age</th><th className="py-2 pr-3"></th>
          <th className="py-2 pr-3">Token</th><th className="py-2 pr-3 text-right">Amount</th>
          <th className="py-2 pr-3">From</th><th className="py-2">To</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.hash}-${i}`} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 pr-3 whitespace-nowrap"><HashLink explorer={explorer} hash={r.hash} /></td>
              <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap" title={r.time}>{fmtTime(r.time)}</td>
              <td className="py-2 pr-3"><DirBadge d={r.direction} /></td>
              <td className="py-2 pr-3 whitespace-nowrap">
                <Link to={`/explorer/${chain}/token/${r.token.address}`} className="font-semibold text-xs text-primary hover:underline">{r.token.symbol}</Link>
                <span className="text-[10px] text-muted-foreground ml-1.5">{trunc(r.token.address, 4)}</span>
              </td>
              <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtNum(r.amount)}</td>
              <td className="py-2 pr-3 whitespace-nowrap"><AddrLink chain={chain} address={r.from} self={r.from === address} /></td>
              <td className="py-2 whitespace-nowrap"><AddrLink chain={chain} address={r.to} self={r.to === address} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && rows.length === 0 && <Skeleton className="h-40 w-full mt-2" />}
      <LoadMore hasMore={hasMore} loading={loading} onClick={loadMore} testid="transfers-load-more-btn" />
    </div>
  );
}

function InternalTab({ chain, address, explorer }) {
  const fetchPage = useCallback((p) => mcGetInternal(chain, address, { page: p, offset: 25 }), [chain, address]);
  const { rows, loading, hasMore, meta, loadMore } = usePaged(fetchPage);
  if (meta?.unsupported) return <UnsupportedNote reason={meta.reason} />;
  if (!loading && rows.length === 0) return <EmptyState text="No internal transactions found" />;
  return (
    <div className="overflow-x-auto" data-testid="mc-internal-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
          <th className="py-2 pr-3">Parent Tx</th><th className="py-2 pr-3">Age</th><th className="py-2 pr-3"></th>
          <th className="py-2 pr-3">From</th><th className="py-2 pr-3">To</th><th className="py-2 text-right">Value</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.hash}-${i}`} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 pr-3 whitespace-nowrap"><HashLink explorer={explorer} hash={r.hash} /></td>
              <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap" title={r.time}>{fmtTime(r.time)}</td>
              <td className="py-2 pr-3"><DirBadge d={r.direction} /></td>
              <td className="py-2 pr-3 whitespace-nowrap"><AddrLink chain={chain} address={r.from} self={r.from === address} /></td>
              <td className="py-2 pr-3 whitespace-nowrap"><AddrLink chain={chain} address={r.to} self={r.to === address} /></td>
              <td className="py-2 text-right font-mono text-xs whitespace-nowrap">{fmtNum(r.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && rows.length === 0 && <Skeleton className="h-40 w-full mt-2" />}
      <LoadMore hasMore={hasMore} loading={loading} onClick={loadMore} testid="internal-load-more-btn" />
    </div>
  );
}

function DexTab({ chain, address, explorer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    mcGetDexTrades(chain, address).then(setData).catch(() => toast.error('Failed to load DEX trades')).finally(() => setLoading(false));
  }, [chain, address]);
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (data?.unsupported) return <UnsupportedNote reason={data.reason} />;
  const trades = data?.trades || [];
  if (trades.length === 0) {
    return data?.explorer_error
      ? <UnsupportedNote reason={data.explorer_error} />
      : <EmptyState text="No DEX trades detected in recent activity" />;
  }
  return (
    <div data-testid="mc-dex-table">
      {data.explorer_error && (
        <p className="text-xs text-amber-500 mb-2" data-testid="mc-dex-explorer-warning">{data.explorer_error}</p>
      )}
      <p className="text-xs text-muted-foreground mb-3">{data.note}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Tx</th><th className="py-2 pr-3">Age</th><th className="py-2 pr-3">Side</th>
            <th className="py-2 pr-3">Token</th><th className="py-2 pr-3 text-right">Amount</th>
            <th className="py-2 pr-3 text-right">Paid / Received</th><th className="py-2 text-right">USD</th>
          </tr></thead>
          <tbody>
            {trades.map((t, i) => (
              <tr key={`${t.hash}-${i}`} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-2 pr-3 whitespace-nowrap"><HashLink explorer={explorer} hash={t.hash} /></td>
                <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap" title={t.time}>{fmtTime(t.time)}</td>
                <td className="py-2 pr-3">
                  <Badge className={`text-[10px] px-1.5 ${t.side === 'buy' ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/40' : 'bg-red-500/15 text-red-500 border-red-500/40'}`} variant="outline">
                    {t.side.toUpperCase()}
                  </Badge>
                </td>
                <td className="py-2 pr-3 whitespace-nowrap font-semibold text-xs">
                  <Link to={`/explorer/${chain}/token/${t.token.address}`} className="text-primary hover:underline">{t.token.symbol}</Link>
                </td>
                <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtNum(t.amount)}</td>
                <td className="py-2 pr-3 text-right font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {t.quote_amount != null ? `${fmtNum(t.quote_amount)} ${t.quote_symbol}` : '—'}
                </td>
                <td className="py-2 text-right font-mono text-xs whitespace-nowrap" title={t.value_source || ''}>
                  {t.usd != null ? fmtUsd(t.usd) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PortfolioTab({ chain, portfolio, loading }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!portfolio) return <EmptyState text="Portfolio unavailable" />;
  const { native, tokens = [] } = portfolio;
  return (
    <div data-testid="mc-portfolio">
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-2xl font-bold" data-testid="portfolio-total-usd">{fmtUsd(portfolio.total_usd)}</span>
        <span className="text-xs text-muted-foreground">total · {portfolio.priced_tokens}/{portfolio.token_count} tokens priced</span>
      </div>
      {portfolio.limited && <UnsupportedNote reason={portfolio.limited} />}
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Asset</th><th className="py-2 pr-3 text-right">Balance</th>
            <th className="py-2 pr-3 text-right">Price</th><th className="py-2 text-right">Value</th>
          </tr></thead>
          <tbody>
            <tr className="border-b border-border/50 bg-muted/20">
              <td className="py-2.5 pr-3 font-semibold text-xs">{native.symbol} <span className="text-[10px] text-muted-foreground">native</span></td>
              <td className="py-2.5 pr-3 text-right font-mono text-xs">{fmtNum(native.balance)}</td>
              <td className="py-2.5 pr-3 text-right font-mono text-xs">{native.price_usd ? fmtUsd(native.price_usd) : '—'}</td>
              <td className="py-2.5 text-right font-mono text-xs">{fmtUsd(native.usd)}</td>
            </tr>
            {tokens.map((t) => (
              <tr key={t.address} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-2.5 pr-3 whitespace-nowrap">
                  <Link to={`/explorer/${chain}/token/${t.address}`} className="font-semibold text-xs text-primary hover:underline">{t.symbol}</Link>
                  <span className="text-[10px] text-muted-foreground ml-1.5">{t.name ? `${t.name} · ` : ''}{trunc(t.address, 4)}</span>
                </td>
                <td className="py-2.5 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtNum(t.balance)}</td>
                <td className="py-2.5 pr-3 text-right font-mono text-xs whitespace-nowrap">{t.price_usd ? fmtUsd(t.price_usd) : '—'}</td>
                <td className="py-2.5 text-right font-mono text-xs whitespace-nowrap">{t.usd != null ? fmtUsd(t.usd) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PnlTab({ chain, address }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    mcGetPnl(chain, address).then(setData).catch(() => toast.error('Failed to load PnL')).finally(() => setLoading(false));
  }, [chain, address]);
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (data?.unsupported) return <UnsupportedNote reason={data.reason} />;
  const tokens = data?.tokens || [];
  if (tokens.length === 0) {
    return data?.explorer_error
      ? <UnsupportedNote reason={data.explorer_error} />
      : <EmptyState text="No DEX trading activity detected — nothing to compute" />;
  }
  const t = data.totals;
  const pnlColor = (v) => (v > 0 ? 'text-emerald-500' : v < 0 ? 'text-red-500' : 'text-muted-foreground');
  return (
    <div data-testid="mc-pnl">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          ['Total Buys', fmtUsd(t.buys_usd), 'text-foreground'],
          ['Total Sells', fmtUsd(t.sells_usd), 'text-foreground'],
          ['Holdings Value', fmtUsd(t.holdings_usd), 'text-foreground'],
          ['Est. PnL', `${t.pnl_usd >= 0 ? '+' : ''}${fmtUsd(t.pnl_usd)}`, pnlColor(t.pnl_usd)],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`text-lg font-bold font-mono ${cls}`}>{val}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-3">{data.note}</p>
      {data.explorer_error && (
        <p className="text-xs text-amber-500 mb-3" data-testid="mc-pnl-explorer-warning">{data.explorer_error}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Token</th><th className="py-2 pr-3 text-right">Trades</th>
            <th className="py-2 pr-3 text-right">Bought ($)</th><th className="py-2 pr-3 text-right">Sold ($)</th>
            <th className="py-2 pr-3 text-right">Holding</th><th className="py-2 pr-3 text-right">Holding ($)</th>
            <th className="py-2 text-right">Est. PnL</th>
          </tr></thead>
          <tbody>
            {tokens.map((r) => (
              <tr key={r.address} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-2 pr-3 font-semibold text-xs whitespace-nowrap">{r.symbol} <span className="text-[10px] text-muted-foreground font-normal">{trunc(r.address, 4)}</span></td>
                <td className="py-2 pr-3 text-right font-mono text-xs">{r.trades}</td>
                <td className="py-2 pr-3 text-right font-mono text-xs">{fmtUsd(r.buys_usd)}</td>
                <td className="py-2 pr-3 text-right font-mono text-xs">{fmtUsd(r.sells_usd)}</td>
                <td className="py-2 pr-3 text-right font-mono text-xs">{fmtNum(r.holding)}</td>
                <td className="py-2 pr-3 text-right font-mono text-xs">{fmtUsd(r.holding_usd)}</td>
                <td className={`py-2 text-right font-mono text-xs font-semibold ${pnlColor(r.pnl_usd)}`}>
                  {r.pnl_usd >= 0 ? '+' : ''}{fmtUsd(r.pnl_usd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SolActivityTab({ chain, address, explorer }) {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (before) => {
    setLoading(true);
    try {
      const d = await mcGetTransactions(chain, address, before ? { cursor: before, offset: 25 } : { offset: 25 });
      setRows((r) => (before ? [...r, ...(d.rows || [])] : d.rows || []));
      setCursor(d.next_cursor);
      setHasMore(!!d.next_cursor);
    } catch {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [chain, address]);

  useEffect(() => { load(null); }, [load]);
  if (!loading && rows.length === 0) return <EmptyState text="No activity found" />;
  return (
    <div className="overflow-x-auto" data-testid="mc-sol-activity">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
          <th className="py-2 pr-3">Signature</th><th className="py-2 pr-3">Age</th>
          <th className="py-2 pr-3">Slot</th><th className="py-2">Status</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.signature}-${i}`} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 pr-3 whitespace-nowrap">
                <a href={`${explorer}/tx/${r.signature}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-primary hover:underline">
                  {trunc(r.signature, 10)}
                </a>
              </td>
              <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap" title={r.time}>{fmtTime(r.time)}</td>
              <td className="py-2 pr-3 font-mono text-xs">{r.slot?.toLocaleString?.() || r.slot}</td>
              <td className="py-2">
                <Badge variant="outline" className={`text-[10px] px-1.5 ${r.status === 'success' ? 'text-emerald-500 border-emerald-500/40' : 'text-red-500 border-red-500/40'}`}>
                  {r.status.toUpperCase()}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && rows.length === 0 && <Skeleton className="h-40 w-full mt-2" />}
      <LoadMore hasMore={hasMore} loading={loading} onClick={() => load(cursor)} testid="sol-load-more-btn" />
    </div>
  );
}

// ---------- main page ----------
export default function MCAddressPage() {
  const { chain, address } = useParams();
  const [ov, setOv] = useState(null);
  const [ovError, setOvError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState(null);

  useEffect(() => {
    setLoading(true); setOvError(null); setOv(null);
    setPortfolio(null); setPortfolioLoading(true); setTab(null);
    mcGetOverview(chain, address)
      .then(setOv)
      .catch((e) => setOvError(e?.response?.data?.detail || 'Failed to load wallet'))
      .finally(() => setLoading(false));
    mcGetPortfolio(chain, address)
      .then(setPortfolio)
      .catch(() => {})
      .finally(() => setPortfolioLoading(false));
    // Safety timeout: never leave the Portfolio tile spinning forever if the
    // aggregator is slow on a very large wallet — fall back to the native value.
    const portfolioTimeout = setTimeout(() => setPortfolioLoading(false), 20000);
    return () => clearTimeout(portfolioTimeout);
  }, [chain, address]);

  const conf = ov?.chain;
  const isSol = conf?.kind === 'solana';
  // Capability, not caveat: Arc carries a `limited` note (windowed reads) yet
  // serves full history — only chains with no history index at all drop to
  // portfolio-only.
  const hasHistory = conf?.has_history !== false;

  const tabs = isSol
    ? [['activity', 'Activity'], ['portfolio', 'Portfolio']]
    : hasHistory
      ? [['transactions', 'Transactions'], ['transfers', 'Token Transfers'], ['internal', 'Internal'],
         ['dex', 'DEX Trades'], ['portfolio', 'Portfolio'], ['pnl', 'PnL']]
      : [['portfolio', 'Portfolio']];
  const activeTab = tab || tabs[0]?.[0];

  const onCopy = async () => {
    await copyToClipboard(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-64" /><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (ovError) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center" data-testid="mc-address-error">
        <p className="text-red-500 mb-4">{ovError}</p>
        <Link to="/explorer"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Explorer</Button></Link>
      </div>
    );
  }

  const ins = ov.insights;
  const explorerAddrUrl = isSol ? `${conf.explorer}/account/${address}` : `${conf.explorer}/address/${address}`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="mc-address-page">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/explorer">
          <Button variant="ghost" size="sm" data-testid="back-to-explorer-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Explorer</Button>
        </Link>
        <Badge variant="outline" style={{ color: conf.accent, borderColor: `${conf.accent}66` }} data-testid="chain-badge">
          {conf.label}
        </Badge>
        <Badge variant="outline" className="text-[10px] text-muted-foreground">{conf.source_label}</Badge>
        {ov.is_contract && <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-400/50">CONTRACT</Badge>}
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-mono text-sm sm:text-base break-all" data-testid="mc-address-value">{address}</h1>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onCopy} data-testid="copy-address-btn">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
          {ov.label?.label && (
            <div className="flex items-center gap-1.5 mt-1 flex-wrap" data-testid="mc-address-label">
              <Badge className="bg-primary/15 text-primary border-primary/40" variant="outline">{ov.label.label}</Badge>
              {(ov.label.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ov.is_contract && (
            <Link to={`/explorer/${chain}/token/${address}`}>
              <Button variant="outline" size="sm" data-testid="view-as-token-btn">
                <Coins className="w-3.5 h-3.5 mr-1" /> View as Token
              </Button>
            </Link>
          )}
          <a href={explorerAddrUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="external-explorer-btn">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> {conf.explorer_label || 'Explorer'}
            </Button>
          </a>
          <ShareToTelegram kind="addr" payload={address} label="Open in Bot" />
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card data-testid="tile-native-balance">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground"><Wallet className="w-3 h-3" /> {ov.native.symbol} Balance</div>
            <div className="text-lg font-bold font-mono mt-1">{fmtNum(ov.native.balance)}</div>
            <div className="text-xs text-muted-foreground">{fmtUsd(ov.native.usd)}</div>
          </CardContent>
        </Card>
        <Card data-testid="tile-portfolio-value">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground"><Wallet className="w-3 h-3" /> Portfolio Value</div>
            <div className="text-lg font-bold font-mono mt-1">
              {portfolioLoading ? <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" /> : fmtUsd(portfolio?.total_usd ?? ov.native.usd)}
            </div>
            <div className="text-xs text-muted-foreground">{portfolio ? `${portfolio.token_count} tokens` : ''}</div>
          </CardContent>
        </Card>
        <Card data-testid="tile-wallet-age">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground"><Clock className="w-3 h-3" /> Wallet Age</div>
            <div className="text-lg font-bold mt-1">{ins?.age_days != null ? `${ins.age_days}d` : '—'}</div>
            <div className="text-xs text-muted-foreground">{ins?.first_seen ? `first seen ${fmtTime(ins.first_seen)}` : isSol ? 'n/a on Solana' : ''}</div>
          </CardContent>
        </Card>
        <Card data-testid="tile-funded-by">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground"><Droplets className="w-3 h-3" /> Funded By</div>
            <div className="text-sm font-mono mt-1.5">
              {ins?.funded_by ? <AddrLink chain={chain} address={ins.funded_by.address} /> : <span className="text-muted-foreground">—</span>}
            </div>
            <div className="text-xs text-muted-foreground">
              {ins?.funded_by?.amount != null ? `${fmtNum(ins.funded_by.amount)} ${ov.native.symbol}` : ins?.funded_by ? 'first counterparty' : ''}
            </div>
          </CardContent>
        </Card>
      </div>

      {conf.limited && <UnsupportedNote reason={conf.limited} />}

      {/* Tabs */}
      <div className="flex gap-1.5 flex-wrap border-b border-border pb-2" data-testid="mc-tabs">
        {tabs.map(([id, label]) => (
          <Button
            key={id}
            variant={activeTab === id ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setTab(id)}
            data-testid={`mc-tab-${id}`}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="min-h-[240px]">
        {activeTab === 'transactions' && <TxTab chain={chain} address={address.toLowerCase()} explorer={conf.explorer} />}
        {activeTab === 'transfers' && <TransfersTab chain={chain} address={address.toLowerCase()} explorer={conf.explorer} />}
        {activeTab === 'internal' && <InternalTab chain={chain} address={address.toLowerCase()} explorer={conf.explorer} />}
        {activeTab === 'dex' && <DexTab chain={chain} address={address.toLowerCase()} explorer={conf.explorer} />}
        {activeTab === 'portfolio' && <PortfolioTab chain={chain} portfolio={portfolio} loading={portfolioLoading} />}
        {activeTab === 'pnl' && <PnlTab chain={chain} address={address.toLowerCase()} />}
        {activeTab === 'activity' && <SolActivityTab chain={chain} address={address} explorer={conf.explorer} />}
      </div>
    </div>
  );
}
